/**
 * dsh-widgets — Widget contract (shared, stable core).
 *
 * This module defines the ONE contract every widget unit must satisfy:
 * a `Widget` descriptor (default-exported by `src/widgets/<id>/index.ts`)
 * plus the pure resolver helpers the shell and the registry consumers use.
 *
 * The descriptor is deliberately TYPE-ONLY for everything a widget renders;
 * the machine-readable part (id / group / builtin / sizes / defaultInstalled
 * / per-widget locale) lives in each unit's `manifest.json`, which the
 * build-time discovery generator (`scripts/gen-registry.mjs`) reads to emit
 * `src/client/generated.registry.ts`. Neither side is a widget's full
 * definition alone — together they are the unit's contract.
 *
 * Everything in this file is part of the STABLE shared layer:
 *   - Core / Runtime (contract, resolvers)
 *   - Shared Types   (WidgetStats, WidgetRenderOut, WidgetChart, …)
 * It must not import widgets (units import it, never the other way).
 */

import { t } from '../i18n'

/** One usage item from the OpenCode Go usage endpoint. */
export interface UsageItem {
  status: string
  percent: number
  resetsAt: string
}

/** The OpenCode Go usage payload (subset the widget reads). */
export interface UsageData {
  usage: {
    rolling: UsageItem
    weekly: UsageItem
    monthly: UsageItem
  }
}

/** One pooled key's usage snapshot (tail masked, never the full secret). */
export interface UsageKeyEntry {
  ref: string
  label: string
  tail?: string
  data: UsageData | null
}

/** All pooled keys' usage plus a host-computed 共同用量 (proportional mean). */
export interface UsageMulti {
  total: UsageData | null
  keys: UsageKeyEntry[]
}

/** `GET /alpha/whoami` — current Command Code account / org identity. */
export interface CommandCodeWhoami {
  success?: boolean
  user?: { id?: string; name?: string; email?: string; userName?: string } | null
  org?: {
    id?: string
    name?: string
    [k: string]: unknown
  } | null
}

/** `GET /alpha/usage/summary` — request counts, success rate, tokens, spend. */
export interface CommandCodeUsageSummary {
  totalCount?: number
  totalCost?: number
  averageCost?: number
  successRate?: number
  completedCount?: number
  failedCount?: number
  totalTokensIn?: number
  totalTokensOut?: number
  totalTokens?: number
  totalCredits?: number
  totalFreeCredits?: number
  totalMonthlyCredits?: number
  totalPurchasedCredits?: number
  periodBasis?: string
}

/** One Command Code window limit (5h / weekly): used vs cap + reset time. */
export interface CommandCodeWindow {
  used?: number
  cap?: number
  exceeded?: boolean
  /** Epoch ms; the wall-clock time the window resets. */
  resetAt?: number
}

/** `GET /alpha/billing/credits` — credit balance + 5h / weekly quota windows. */
export interface CommandCodeCredits {
  credits?: {
    belowThreshold?: boolean
    creditThreshold?: number
    monthlyCredits?: number
    purchasedCredits?: number
    freeCredits?: number
  } | null
  windowLimits?: {
    limited?: boolean
    exceeded?: unknown
    fiveHour?: CommandCodeWindow | null
    weekly?: CommandCodeWindow | null
  } | null
}

/** `GET /alpha/billing/subscriptions` — plan + billing-period end. */
export interface CommandCodeSubscription {
  success?: boolean
  data?: {
    id?: string
    status?: string
    planId?: string
    priceId?: string
    quantity?: number
    cancelAtPeriodEnd?: boolean
    currentPeriodStart?: string
    currentPeriodEnd?: string
    endedAt?: string | null
    canceledAt?: string | null
  } | null
}

/** The aggregated Command Code account payload from the host
 *  `/api/commandcode-usage` route (the four official endpoints). Every slice
 *  is nullable: the host fetches them independently, so one failing endpoint
 *  never blanks the others. */
export interface CommandCodeData {
  whoami: CommandCodeWhoami | null
  usage: CommandCodeUsageSummary | null
  credits: CommandCodeCredits | null
  subscription: CommandCodeSubscription | null
}

/** One hardware snapshot from the Host `/api/sysinfo` route (machine-local
 *  values only — never session data). `cpu.util` is the utilization averaged
 *  over the window between two host samples (null on the very first sample,
 *  before a delta exists). */
export interface SysInfo {
  ts: number
  cpu: { util: number | null }
  mem: { used: number; total: number; percent: number }
  gpu: {
    name: string
    temp: number
    util: number
    memUsed: number
    memTotal: number
    memPercent: number
  } | null
  /** Rolling sample history (host ring buffer, newest last, ≤120 samples) for
   *  the utilization sparklines. `null` = no baseline yet at that sample. */
  history?: {
    ts: number[]
    cpu: Array<number | null>
    gpu: Array<number | null>
  }
}

/** Session stats a widget render can read. */
export interface WidgetStats {
  turns: number
  steps: number
  llmMs: number
  toolMs: number
  ttftMs: number
  ttftSteps: number
  decodeMs: number
  decodeTokens: number
  usage: { inputTokens: number; cacheReadTokens: number; outputTokens: number } | null
  usageData: UsageData | null
  /** Multi-key pool usage (OpenCode Go): every key + host-computed total. */
  usageMulti?: UsageMulti | null
  /** Command Code account usage (whoami / summary / credits / subscription),
   *  aggregated by the host `/api/commandcode-usage` route. */
  commandCode?: CommandCodeData | null
  /** Command Code host-route error surfaced to the widgets: `null` when the
   *  last fetch succeeded, or a stable code ('unconfigured' | 'unloaded' |
   *  'unavailable') when it did not, so cards can say WHY instead of a bare
   *  「未配置」 (the key is auto-read host-side, never user-entered). */
  commandCodeError?: string | null
  /** Current pooled view selection: 'total' or a `poolModes` entry ('Key 1'…). */
  poolView?: string
  /** Selectable pooled views in cycle order; first entry must be 'total'. */
  poolModes?: string[]
  /** Optional live context pressure 0..1 (from useProjection('contextPressure')). */
  contextPercent?: number | null
  contextWindow?: number | null
  contextTokens?: number | null
  /** Optional composition of the next request (system/tools/messages), from useProjection('contextBreakdown'). */
  contextBreakdown?: { systemTokens: number; toolsTokens: number; messageTokens: number } | null
  /** Last ~13 weeks of daily token usage (self-accounted). */
  heatmapGrid?: Array<Array<{ value: number; date: string }>>
  /** Raw self-accounted daily token log (dateKey -> value), so wide (2×4
   *  half-year) and bar (last-7-day) variants can derive their own grids from
   *  the same source the 2×2 calendar uses. */
  heatmapRaw?: Record<string, number>
  /** Id of the currently armed (awaiting second tap) action, if any. */
  armedAction?: string | null
  /** Current task list (todos projection): status is pending | in_progress | completed. */
  todos?: Array<{ content: string; status: 'pending' | 'in_progress' | 'completed' }> | null
  /** Rolling window of the live conversation trajectory (oldest first, newest
   *  last, at most `TRAJECTORY_WINDOW` beats). Re-derived on every node/timeline
   *  change, so in-flight model steps and tool calls appear as they run. */
  trajectory?: TrajectoryBeat[]
  /** Per-instance config merged by the shell (typed any: widgets with a
   *  configSchema read their keys from the same record the collector feeds). */
  [key: string]: unknown
}

/** One beat of the conversation trajectory: which lane fired, and how long it
 *  took. Mirrors the official 轨迹 timeline's three lanes (输入 / 模型 / 工具):
 *  an input message has no duration of its own (0), a model step spans its
 *  stepStartTime → completedTime, a tool call spans callTime → result. */
export interface TrajectoryBeat {
  kind: 'input' | 'model' | 'tool'
  /** Duration in ms; 0 for a user/steering message (the input lane is instant). */
  ms: number
}

/** Size of the rolling trajectory window: how many beats a 对话轨迹 card shows
 *  and therefore how many fixed slots the lanes chart draws. The collector
 *  trims to it and the renderer keeps the slot width stable at it. */
export const TRAJECTORY_WINDOW = 30

/** One traced segment of a `lanes` chart: a beat plus its identity in the
 *  rolling window (the widget passes beats oldest→newest; the renderer derives
 *  the slot from the ARRAY INDEX, so index 0 is the oldest beat shown). */
export interface LaneDatum {
  kind: TrajectoryBeat['kind']
  /** Hover tooltip text (e.g. `模型 3.2s`); the widget owns its wording. */
  label?: string
  /** Beat duration in ms. Read only when the chart asks for `laneSizing: 'time'`
   *  (0 for an input, which then collapses to a minimum-width tick). */
  ms?: number
}

/** One bar for a mini bar chart. */
export interface BarDatum {
  label: string
  value: number
  /** 0..1 used for the fill ratio; falls back to value/max when absent. */
  ratio?: number
  tone?: 'primary' | 'success' | 'warn' | 'danger' | 'muted'
}

/** A chart block a card body can render (declarative, theme tokens only). */
export interface WidgetChart {
  kind: 'bars' | 'ring' | 'rings' | 'line' | 'segments' | 'heatmap' | 'barsV' | 'figures' | 'lanes'
  bars?: BarDatum[]
  /** Trajectory lanes (对话轨迹): one bar per beat, newest at the RIGHT, colored
   *  by the official 轨迹 lane colors (输入 / 模型 / 工具). No axes, no corner
   *  labels — the bars own the whole remaining card height. */
  lanes?: LaneDatum[]
  /** How `lanes` distributes width along the shared time axis (per-instance
   *  choice in the widget's own config):
   *   - 'time'  (default) — each beat's width is proportional to its duration, so
   *     a long tool call visibly owns more of the lane than a quick model step
   *     (an input, ~0ms, collapses to a minimum-width tick);
   *   - 'equal' — the fixed-slot window: every beat is one slot wide, the slot
   *     freezes at TRAJECTORY_WINDOW beats, and the row stops re-scaling as the
   *     window rolls. */
  laneSizing?: 'equal' | 'time'
  /** Donut row (e.g. OpenCode rolling/weekly/monthly, Command Code 5h/weekly/
   *  monthly). `label` renders as a SMALL GREY caption beside the percent —
   *  leave it empty when the figure should stand alone; `name` then carries the
   *  datum's identity into the hover tooltip only. `decimals` overrides the
   *  percent precision (default 0 = whole numbers). */
  rings?: Array<{ label: string; value: number; ratio?: number; tone?: 'primary' | 'success' | 'warn' | 'danger' | 'muted'; decimals?: number; name?: string }>
  /** For ring: one datum + its centered label. */
  value?: number
  valueLabel?: string
  max?: number
  /** For figures: a row of label-over-value figure pairs (e.g. 今日用量 24.7M /
   *  今日推荐 200M) — plain numbers, no axis, spread across the card width. */
  figures?: Array<{ label: string; value: string; tone?: BarDatum['tone'] }>
  /** Utilization sparkline (Windows-task-manager style): a filled area under
   *  a polyline. `null` entries break the line (no baseline at that sample). */
  line?: {
    values: Array<number | null>
    /** Y-scale maximum (default 100: percentages). */
    max?: number
    /** Endpoint labels: [earliest, latest] short times ("14:02" / "14:22"). */
    labels?: [string, string]
  }
  /** Segmented bar (system/tools/messages). Each segment has a token share. */
  segments?: Array<{ label: string; tokens: number; tone: 'primary' | 'success' | 'muted' | 'warn' }>
  totalTokens?: number
  /** Heatmap grid rows (per day amounts). */
  heatmap?: Array<Array<{ value: number; date: string }>>
}

/** An interactive action on a card (e.g. one-click Compact). */
export interface WidgetAction {
  id: string
  label: string
  kind?: 'primary' | 'danger' | 'ghost'
  /** Two-step confirm: first click arms, second click fires. */
  confirmHint?: string
}

/** A freeform rich block (photo / quote / inspiration). */
export interface WidgetRich {
  type: 'quote' | 'image'
  text?: string
  src?: string
  /** Quote text alignment (per-card config). */
  align?: 'left' | 'center' | 'right'
  /** Vertical placement of the quote block. */
  valign?: 'top' | 'center' | 'bottom'
  /** Allow line wrapping. */
  wrap?: boolean
}

/** A corner action button (two-tap confirm: circle → armed capsule). */
export interface WidgetCorner {
  id: string
  label: string
  armedLabel: string
  armed: boolean
  /** Which corner: top-right (default) or bottom-right. */
  pos?: 'top' | 'bottom'
}

/** Supported card sizes. All cards share one grid-unit height; 2×4 is twice as
 *  wide as 2×2 (2 grid-unit rows tall, 1 wide → the render only differs in
 *  placement/length, never in height). */
export type WidgetSize = '2x2' | '2x4'

/** Extra render context. `sim` lets a preview force a widget into a specific
 *  state (e.g. peak-pricing preview toggling EXPENSIVE/CHEAP) so its states can
 *  be reviewed without waiting for the real condition. */
export interface WidgetRenderMeta {
  size?: WidgetSize
  sim?: Record<string, unknown>
}

/** Instance key = `${widgetId}@${size}` (e.g. `context-water@2x4`). Even the same
 *  widget at two sizes is two independent, co-installable instances. */
export function instanceKey(widgetId: string, size: WidgetSize): string {
  return `${widgetId}@${size}`
}

/** Parse an instance key back into its widget id and size. Unknown sizes fall
 *  back to '2x2' so legacy persisted ids (which are bare widget ids) still work. */
export function parseInstanceKey(key: string): { widgetId: string; size: WidgetSize } {
  const at = key.lastIndexOf('@')
  if (at <= 0) return { widgetId: key, size: '2x2' }
  const size = key.slice(at + 1)
  return size === '2x4' ? { widgetId: key.slice(0, at), size: '2x4' } : { widgetId: key.slice(0, at), size: '2x2' }
}

/** The card shape a widget render produces. */
export interface WidgetRenderOut {
  title: string
  title2?: string
  /** Optional text shown at the right end of the title row (e.g. ~613K / 1M).
   *  Whenever it is DEFINED the card's `value` moves up into that same row
   *  (instead of sitting in the body) — pass `''` to place the big figure at the
   *  top-right corner with no extra caption of its own. */
  headRight?: string
  /** Optional prominent figure rendered on its own row UNDER the title (e.g. the
   *  context percent, with small figures beside it). Pushes content top-aligned. */
  headAfter?: { big?: string; small?: string }
  /** Optional small caption directly under the title that does NOT affect the
   *  vertical alignment (unlike headAfter) — for subtitles like "今日 12.2K". */
  legend?: string
  /** Optional two-line meter under the title (e.g. peak-pricing windows). The
   *  active line lights up (brand blue, slightly enlarged); idle lines keep the
   *  faint legend look. */
  meter?: Array<{ label: string; active?: boolean }>
  value?: string
  /** Value color override (e.g. 'danger' renders the value in the error red,
   *  used by the peak-pricing EXPENSIVE state). */
  valueTone?: 'danger'
  /** Slow red blink on the VALUE itself (e.g. peak pricing is live, or a plan
   *  projected past 100%): the text pulses between full and ~35% opacity in the
   *  error red. Text-level escalation — it deliberately does NOT paint the card. */
  valuePulse?: boolean
  sub?: string
  chart?: WidgetChart
  actions?: WidgetAction[]
  rich?: WidgetRich
  /** Top-right corner capsule/round button (e.g. one-click Compact). */
  corner?: WidgetCorner
  /** Whole-card tap cycles this card through its pooled key views (usage widgets):
   *  total → Key 1 → Key 2 → … → total. Pressing plays a springy press-down.
   *  `store` names the cardConfigs field the selection persists to (default
   *  'poolView'); sys widgets use their own field (e.g. 'bigMetric') so their
   *  cycle never collides with the usage pool view nor fires multikey calls. */
  cycle?: { modes: string[]; current: string; hint: string; store?: string }
  /**
   * The card's data source has not answered yet: paint the LOADING SKELETON
   * (rounded placeholder pills in the card's own layout rhythm) instead of the
   * body. Only `title` is read from the render in that state.
   *
   * The SHELL decides this, not the widget: a widget cannot tell "my data
   * source is still in flight" from "my data source answered with nothing", and
   * those two deserve different cards (placeholder vs 数据不足).
   */
  skeleton?: boolean
  /** Placeholder body rows the skeleton draws under the title/value pills.
   *  Default 2; the shell picks a shape that matches the card family. */
  skeletonRows?: number
}

/** A per-card configuration field rendered in the 组件配置 tab. Text fields
 *  are thunks so 组件配置 re-localizes on language switches. */
export interface ConfigField {
  key: string
  label: string | (() => string)
  type: 'text' | 'textarea' | 'toggle' | 'align' | 'valign' | 'mode'
  default?: string | boolean | 'left' | 'center' | 'right'
  /** For type 'mode': the selectable options as [value, label] pairs. */
  options?: Array<[string, string | (() => string)]>
}

/**
 * Widget-owned preview data (「Example」) — what the market / config preview
 * surfaces feed this widget so every state renders without the real condition.
 *
 * Roles (deliberate, see ARCH-001 §14):
 *  - dev reference: a future agent reads `example` to understand the widget's
 *    input shape without running the app;
 *  - preview mock: the shell merges `stats` over the shared preview stats and
 *    seeds `sim` as the initial simulated state.
 *
 * Convention: if `sim` holds exactly ONE boolean value, clicking the preview
 * toggles it (used by stateful widgets like peak-pricing).
 */
export interface WidgetExample {
  /** Extra stats merged over the shared preview stats. `Partial<WidgetStats>`
   *  is not enough for widgets whose preview depends on config (heatmap window
   *  alignment): a function receives the current per-instance config at preview
   *  time and returns the extra stats. */
  stats?: Partial<WidgetStats> | ((config: Record<string, unknown>) => Partial<WidgetStats>)
  /** Initial simulated state served to `render(meta.sim)`. */
  sim?: Record<string, unknown>
  /** Optional hint shown under the preview card. */
  note?: string
}

/** The card shape a widget render produces. */
export interface Widget {
  id: string
  name: string | (() => string)
  desc: string | (() => string)
  builtin: boolean
  group?: string
  badgeLabel?: string | (() => string)
  /** Sizes this widget supports. Defaults to ['2x2'] when omitted. */
  sizes?: WidgetSize[]
  render: (stats: WidgetStats, meta?: WidgetRenderMeta) => WidgetRenderOut | null
  /** When set (label text), the preview surfaces let you click the card to flip
   *  the widget's simulated state (e.g. peak-pricing 高峰/低峰). */
  simToggle?: string | (() => string)
  /** Optional per-card customization fields (shown in 组件配置 when chosen). */
  configSchema?: ConfigField[]
  /** Widget-owned preview data (see WidgetExample). */
  example?: WidgetExample
  /** Reserved: template units are NEVER registered (kept out of the discovery
   *  root structurally; this flag is belt-and-braces for tooling). */
  template?: boolean
}

/** Identity helper with a doc anchor: every widget unit default-exports
 *  `defineWidget({ ... })` so the contract stays self-describing. */
export function defineWidget<W extends Widget>(w: W): W {
  return w
}

/** Resolve a possibly-thunked display label at read time. */
export function resolveLabel(s: string | (() => string) | undefined): string {
  return typeof s === 'function' ? s() : s ?? ''
}

export function widgetName(w: Widget): string {
  return resolveLabel(w.name)
}

export function widgetDesc(w: Widget): string {
  return resolveLabel(w.desc)
}

export function widgetBadgeLabel(w: Widget): string | undefined {
  return typeof w.badgeLabel === 'function' ? w.badgeLabel() : w.badgeLabel
}

export function widgetSimToggle(w: Widget): string | undefined {
  return typeof w.simToggle === 'function' ? w.simToggle() : w.simToggle
}

/** Resolve a config field's label (thunk-aware). */
export function fieldLabel(f: ConfigField): string {
  return resolveLabel(f.label)
}

/** Resolve a mode option's [value, label] pair label. */
export function optionLabel(o: [string, string | (() => string)]): string {
  return resolveLabel(o[1])
}

/** Badge text for a widget. */
export function badgeOf(w: Widget): string {
  return widgetBadgeLabel(w) ?? (w.builtin ? t('badge.system') : t('badge.external'))
}

/** The group key for a widget (its own id when it is not grouped). */
export function groupOf(w: Widget): string {
  return w.group ?? w.id
}

/** The sizes a widget supports, defaulting to 2×2 only. */
export function sizesOf(w: Widget): WidgetSize[] {
  return Array.isArray(w.sizes) && w.sizes.length > 0 ? w.sizes.slice() : ['2x2']
}